import type { NotificationDelivery } from '@bekebi/contracts/api';
import { roleLabel } from '../../session/roles';
import { formatDateTime } from '../services/format';

/**
 * Alert email presentation (Phase 07 Stage 05-C). Every value is the API's: status, attempts,
 * the fixed error code and the times. "Accepted by the mail server" is the strongest claim the
 * portal can make — the mail server took the email; whether it reached an inbox is unknown, so
 * nothing here ever says it was delivered.
 */

export const DELIVERY_KIND_TEXT: Record<NotificationDelivery['kind'], string> = {
  alert_opened: 'Alert email',
  alert_recovered: 'Recovery email',
};

/** What each fixed error code means. Never the mail server's own words. */
export const DELIVERY_ERROR_TEXT: Record<NonNullable<NotificationDelivery['errorCode']>, string> = {
  smtp_unavailable: 'The mail server could not be reached.',
  smtp_timeout: 'The mail server did not answer in time.',
  smtp_tls_error: 'A secure connection to the mail server could not be made.',
  smtp_auth_failed: 'The mail server did not accept the portal’s sign-in.',
  smtp_rejected: 'The mail server refused the email.',
  recipient_rejected: 'The mail server refused the recipient’s address.',
  recipient_unavailable:
    'The recipient is no longer an active Admin or ICT user, so nothing was sent.',
  unexpected_error: 'An unexpected error stopped the email.',
};

type Tone = 'success' | 'danger' | 'warning' | 'neutral';

export function deliveryState(delivery: NotificationDelivery): { label: string; tone: Tone } {
  switch (delivery.status) {
    case 'sent':
      return { label: 'Accepted by the mail server', tone: 'success' };
    case 'failed':
      return { label: 'Not sent', tone: 'danger' };
    case 'sending':
      return { label: 'Sending', tone: 'neutral' };
    case 'pending':
      return delivery.attempts > 0
        ? { label: 'Will try again', tone: 'warning' }
        : { label: 'Waiting to send', tone: 'neutral' };
  }
}

const TONE: Record<Tone, string> = {
  success: 'border-success-line bg-success-soft text-success',
  danger: 'border-danger-line bg-danger-soft text-danger',
  warning: 'border-warning-line bg-warning-soft text-warning',
  neutral: 'border-line-strong bg-raised text-fg-2',
};

export function DeliveryStateBadge({ delivery }: { delivery: NotificationDelivery }) {
  const { label, tone } = deliveryState(delivery);
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONE[tone]}`}
    >
      {label}
    </span>
  );
}

/** The recipient by role and Bekebi User ID — the API never sends their email address. */
export function recipientLabel(delivery: NotificationDelivery): string {
  return `${roleLabel(delivery.recipient.role)} · ${delivery.recipient.bekebiUserId}`;
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** One line on what happened and when: acceptance, the failure and its attempts, or the wait. */
export function deliveryDetail(delivery: NotificationDelivery): string {
  const error = delivery.errorCode ? DELIVERY_ERROR_TEXT[delivery.errorCode] : null;
  switch (delivery.status) {
    case 'sent':
      return `Accepted ${formatDateTime(delivery.sentAt ?? delivery.updatedAt)}.`;
    case 'failed':
      return `${error ?? 'It could not be sent.'} No further attempts after ${plural(delivery.attempts, 'attempt')} · ${formatDateTime(delivery.updatedAt)}.`;
    case 'sending':
      return `Attempt ${delivery.attempts} of ${delivery.maxAttempts} in progress.`;
    case 'pending':
      return delivery.attempts > 0
        ? `${error ?? 'The last attempt failed.'} Attempt ${delivery.attempts} of ${delivery.maxAttempts} failed · ${formatDateTime(delivery.updatedAt)}.`
        : `Planned ${formatDateTime(delivery.createdAt)}.`;
  }
}
